import _ from 'lodash'
import Backbone from 'backbone'
import template from './edit-stack-preview.hbs'
import OnOffToggle from 'scoreboard/components/on-off-toggle'
import i18n from 'scoreboard/components/i18n'
import appDisplay from 'scoreboard/components/app-display'
import dropdown from 'scoreboard/components/dropdown'
import EditStackMenu from './edit-stack-menu'
import Tooltip from 'scoreboard/components/tooltip'
import rangeToPeriods from 'scoreboard/reports/common/range-to-periods'
import arrayToHash from 'scoreboard/components/array-to-hash'
import MultiColumnTableStack from 'scoreboard/reports/common/table/multi-column-table-stack'
import ReportColumnChooser from 'scoreboard/reports/common/report-column-chooser'
import RepeatByForm from './repeat-by-form'
import { defaultRange }
	from 'scoreboard/reports/scorecard-report/scorecard-report-model'
import GroupedPeriodRangeSelector from 'scoreboard/components/designer/grouped-period-range-selector'
import { getFirstValidCalendarAndSubCalendarIds }
	from 'scoreboard/components/designer/valid-group-and-breakdown-calendars'
import * as aggregationTypeOptions from 'scoreboard/datasets/aggregation-type-options'
import SummaryType, {
	isGroupSimilarTimeRanges
} from 'scoreboard/datasets/model/summary-type'
const { VALUE } = SummaryType
import isGeographic from 'scoreboard/components/is-geographic-data-type'

import DataType from 'scoreboard/datasets/model/data-type'
const {
	YES_NO, TEXT, ROLLUP, PERCENT,
	CURRENCY, DATE_TIME, TIME, CALENDAR_PERIOD,
	NUMBER, DATE
} = DataType

import Dataset from 'scoreboard/datasets/model/dataset'
const { TOTAL } = Dataset
import ConfirmPrompt from 'scoreboard/components/confirm-prompt'

const ALL_OPTION_ID = "[null]"

function temporalIdLabelOptionsForSorting(items, relative) {
	return _.map(items, (item, idx) => {
		//use the word "Blank" if empty string
		let itemLabel = item.label == "" ? 'common.blank' : item.label
		//https://github.com/SpiderStrategies/Scoreboard/issues/42669#issuecomment-983790969
		let label = relative ? i18n.get('report.designer.relativePeriodLabelForSorting',
			itemLabel, idx + 1) : itemLabel
		return {id: `${item.id}`, label, data: {index: idx, value: `${item.id}`}}
	})
}

const nonTemporalIdLabelOptionsForSorting = breakdown => _.map(breakdown.get('breakdownLabelsWithId'), opt => {
	//use the word "Blank" if empty string
	let optionLabel = opt.label == "" ? 'common.blank' : opt.label
	return _.defaults({
		label: optionLabel,
		id: opt.id?.replace(/"/g, '\\"')
	}, opt)
})

const EditStackPreview = Backbone.View.extend({

	className: 'edit-stack-preview bootslide-container',

	events: {
		'click .edit' : '_editStack',
		'click .repeat-by' : '_onClickRepeatBy',
		'click .repeat-by-period' : '_onClickRepeatByPeriod',
		'change [name="sortOrder"]' : '_onChangeSortOrder',
		'change [name="groupSortOrder"]' : '_onChangeGroupSortOrder',
		'change [name="summaryType"]' : '_onChangeSummaryType',
		'change select.breakdownSortValue' : '_onChangeBreakdownSortValue',
		'change select[name="sortAggregationType"]' : '_onChangeSortAggregationType'
	},

	/**
	 * Preview dialog when editing a stack
	 *
	 * @param {ReportStack} options.stack the model of a Report Stack
	 * @param {HeaderCell} opts.headerCell the model of a stack header cells
	 * @param {ReportModel} opts.model the model of the designer
	 * @param {ReportTableModel} opts.tableModel the model of the table
	 */
	initialize: function(options = {}) {
		this.options = options
		this.tableModel = this.options.tableModel
		this.headerCell = this.options.headerCell
		this.stack = this.options.stack || this.headerCell.stack
		this.column = this.stack.findColumn(this.headerCell.column)
		//note do not store a reference to the repeat by in this component
		//because changes to the sort will cause a new repeatBy model to be set
		//on the stack making the reference stale

		//if a delete was done, close the preview
		this.stack.once('deleted', () => this.trigger('done'))

		this.listenTo(this.model, 'change', () => {
			//if there was a change to the designer, re-render
			this.render()
			// #40643 Column can get assigned out from under us, due to stack changes,
			// leaving us with a stale copy that will not report changes upward.
			this.column = this.stack.findColumn(this.headerCell.column)
		})
	},

	_templateContext: function() {
		return _.defaults(this.column.attributes)
	},

	_groupingContext: function() {
		let groupingId = null
		if (this.column) {
			//repeatBy stacks won't have this data because they work on multiple columns
			groupingId = this.column.get('groupingId')
		}
		let groupBy = this.model.get('groupBy') || []
		let numberOfGroupings = groupBy.length

		//determine the position of the grouping, if not yet grouping append
		//to end
		let groupingPosition = this.model.groupByPosition(this.column)
		let isGroupingBy = groupingPosition > -1
		if (groupingPosition == -1) {
			groupingPosition = groupBy.length
		}

		let groupingFieldIds = arrayToHash(groupBy)

		let lastUngroupedColumn = false
		if (numberOfGroupings > 0) {
			let numberOfColumns = this.model.allColumns().length
			lastUngroupedColumn = numberOfColumns - numberOfGroupings == 1
		}

		return {
			lastUngroupedColumn,
			groupingId, 	//the grouping id for this stack (if established)
			groupingPosition, 	//the position of group if this stack is being grouped by
			numberOfGroupings,
			groupingFieldIds,	//hash of the stack ids that are being grouped
			isGroupingBy	//true we're grouping by this stack
		}
	},

	render: function() {
		if (!this.column) {
			return this
		}

		this.$el.html(template(this._templateContext()))
		this._renderGrouping()
		this._renderSort()
		this._renderIndent()
		this._renderSummaryType({ type: 'column' })
		this._renderBreakdownOption()
		this._renderShowHeaderOption(this.column)
		return this
	},

	_renderGrouping: function() {
		if (this.stack.isRepeatByStack() || !this.column.canGroupBy()) {
			//you cannot group on a column in a multi layout stack, or if the
			//column doesn't allow it #40466
			return
		}

		let { groupingId, groupingPosition, isGroupingBy, numberOfGroupings } = this._groupingContext()
		let groupBy = _.clone(this.model.get('groupBy') || [])

		let numberOfStacks =  this.model.stacks().length
		if (numberOfStacks-1 <= numberOfGroupings && !isGroupingBy) {
			return //you must have at least 2 more stacks than you do number of groupings
		}

		let hasBreakdowns = !_.isEmpty(this.model.stacks().findStacksWithBreakdowns())

		//handle the show individual records option
		let isStackFirstLevelGrouping = groupingPosition == 0
		if (isGroupingBy && isStackFirstLevelGrouping) {
			//only the first level grouping has the show individual records options
			this.$('.show-individual-records-container').removeClass('is-not-displayed')
			this.$('.bootslide-step.group-container').addClass('has-active-grouping')
			this.showIndividualRecordsToggle = new OnOffToggle({
				name: 'showIndividualRecords',
				checked: this.model.isShowingIndividualRecords(),
				label: 'report.designer.showIndividualRecords',
				isMini: true,
				disabled: hasBreakdowns
			})
			this.$('.show-individual-records-container').append(this.showIndividualRecordsToggle.el)
			this.showIndividualRecordsToggle.render()

			this.listenTo(this.showIndividualRecordsToggle, 'change', enable => {
				const callBack = () => {
					this.model.set('showIndividualRecords', enable)
					if (!enable) setColumnsToValueSummaryType(this.column, this.model)
					//this toggles between aggregating and non aggregation, which
					//similar to grouping is major re-write of the table so remove the preview
					this.trigger('done')
				}

				if (enable && this.model.hasColumnFilters()) {
					const prompt = this.warningPrompt('showRecords', callBack,
						() => this.showIndividualRecordsToggle.val(false))
					appDisplay.dialog(prompt)
				} else { callBack() }
			})

			this.listenTo(this.showIndividualRecordsToggle, 'disabled', () => {
				_.invoke(this.disabledMessageTooltip, 'remove')
				this.disabledMessageTooltip = new Tooltip({
					attachTo: this.showIndividualRecordsToggle.$('.toggle-mini'),
					content: i18n.get('report.designer.cantShowIndividualRecordsAggregated')
				}).render()
			})
		}

		//determine the group by label, if we're not yet grouping, or if we're
		// the first level grouping, show "Group By", otherwise show "Group Again"
		let groupByLabelCode = (numberOfGroupings == 0 || isStackFirstLevelGrouping) ? 'common.group.group' :
			'report.designer.groupAgain'
		// #53570 hold on to the column name for "Number of X" Group toggle purposes
		let numberOfXLabel = null

		//handle the group by option, only show if we're grouping on the stack,
		//or we're not already grouping twice
		if (isGroupingBy || groupBy.length < 2) {
			//evaluate conditions that prevent grouping
			let fieldIdOrColumnType = this.column.fieldIdOrColumnType()
			let isFieldIdOrColumnTypeGrouped = this.model
				.isGroupingByFieldIdOrColumnType(fieldIdOrColumnType)
			let cannotGroupMessageCode

			if (this.stack.isRepeatByStack()) {
				//breakdowns cannot be grouped by
				cannotGroupMessageCode = 'report.designer.cannotGroupBreakdown'
			} else if (!isGroupingBy && isFieldIdOrColumnTypeGrouped) {
				//prevent grouping on an already grouped stack
				cannotGroupMessageCode = 'report.designer.alreadyGrouped'
			} else if ((isGroupingBy && isStackFirstLevelGrouping) && hasBreakdowns) {
				//cannot ungroup with existing breakdowns
				cannotGroupMessageCode = 'report.designer.cantUngroupAggregated'
			} else if (this.column.get('calculated')) {
				//grouping on calculated columns not yet supported
				cannotGroupMessageCode = 'report.designer.cannotGroupCalculated'
			} else if (!_.isNil(this.column.get('datasetFieldId')) &&
				this.column.get('datasetFieldId') == TOTAL) {
				// #53570 prevent grouping based on "Number of X" columns
				cannotGroupMessageCode = 'report.designer.cannotGroupOnNumberOfColumn'
				numberOfXLabel = this.model?.get('dataset')?.get('tracked')
			}

			this.groupToggle = new OnOffToggle({
				name: 'group',
				checked: isGroupingBy,
				label: groupByLabelCode,
				disabled: !_.isNil(cannotGroupMessageCode),
				isMini: true
			})
			this.listenTo(this.groupToggle, 'disabled', () => {
				_.invoke(this.disabledMessageTooltip, 'remove')
				this.disabledMessageTooltip = new Tooltip({
					attachTo: this.groupToggle.$('.toggle-mini'),
					content: !_.isNil(numberOfXLabel) ? i18n.get(cannotGroupMessageCode, numberOfXLabel) : i18n.get(cannotGroupMessageCode)
				}).render()
			})
			this.listenTo(this.groupToggle, 'change', enable => {
				let callBack
				let doShowPrompt
				let prompt

				if (enable) {
					doShowPrompt = !_.isEmpty(this.column.get('datasetFilters'))
					callBack = () => {
						addGroupBy(this.column, this.model, groupBy)

						//grouping causes a major rewrite of the table, so remove preview
						this.trigger('done')
					}
					prompt = this.warningPrompt('groupAgain', callBack,
						() => this.groupToggle.val(false))
				} else {
					doShowPrompt = _.size(this.model.get('groupBy')) == 1 &&
						this.model.hasColumnFilters()
					callBack = () => {
						this.column.unset('groupingId', {silent: true})
						groupBy = _.without(groupBy, groupingId)
						this.model.set('groupBy', groupBy)
					}
					prompt = this.warningPrompt('ungroup', callBack,
						() => this.groupToggle.val(true))
				}
				if (doShowPrompt) { appDisplay.dialog(prompt) } else { callBack() }
			})
			this.$('.group-container')
				.removeClass('is-not-displayed')
				.append(this.groupToggle.el)
			this.groupToggle.render()
		}
	},

	warningPrompt: function(messageKey, onConfirm, onCancel) {
		const prompt = new ConfirmPrompt({
			titleKey: `report.designer.${messageKey}.warningTitle`,
			messageKey: `report.designer.${messageKey}.warningMessage`,
			confirmLabel: `report.designer.${messageKey}.confirm`,
			cancelLabel: 'common.cancel',
			onConfirm
		})
		prompt.once('cancel', onCancel)
		return prompt
	},

	_renderSort: function() {
		if (!this.column.canSortBy()) {
			this.$('.sort-container, .group-sort-container')
				.addClass('is-not-displayed')
			return
		}

		let { isGroupingBy } = this._groupingContext()
		let isAggregatedGroupedColumn = this.column.isAggregatedGroupedColumn(this.model)
		//groupSortType only applies in the non-aggregated case, when results are aggregated
		//they use the "sortType" property
		let hideNonGroupedSort = isGroupingBy && !isAggregatedGroupedColumn

		//setup sort option
		this.$('.sort-container').toggleClass('is-not-displayed', hideNonGroupedSort)
		this.$('.group-sort-container').toggleClass('is-not-displayed', !hideNonGroupedSort)

		let selectContainer = this.$('.sort-select-container')
		let isSorted = this.column.isSorting(this.model)
		selectContainer.toggleClass('is-not-displayed', !isSorted)

		this.sortToggle = new OnOffToggle({
			name: 'sort',
			checked: isSorted,
			label: i18n.get('report.designer.sortUsingThisField'),
			el: this.$('.sort-toggle'),
			isMini: true
		}).render()

		let dataType = this.column.resolveDataType(this.model)
		let sortOptions = getSortOptions(dataType)

		const defaultColumnData = this.column.defaultColumnData(this.model)
		const supportsHierarchyOrdering = _.get(defaultColumnData, 'supportsHierarchyOrdering', false)
		const includeTreeSortingOption = supportsHierarchyOrdering
		if (includeTreeSortingOption) {
			let treeSortLabel = 'report.designer.scorecard.sort.hierarchy'
			if (this.model.reportDomainType == 'dataset') {
				treeSortLabel = 'report.designer.datasets.sort.hierarchy'
			}
			sortOptions.splice(0, 0, {
				id: 'tree',
				label: i18n.get(treeSortLabel)
			})
		}

		dropdown.idLabels(this.$('[name=sortOrder]').empty(),
			sortOptions, this.column.get('sortType'))

		//handle toggling on/off sorting
		this.listenTo(this.sortToggle, 'change', this.onToggleSort.bind(this))

		//setup the breakdown sorting options
		if (this.stack.isRepeatByStack()) {
			let showAggregationSelection = false
			_.each(this.stack.breakdowns(), (breakdown, index) => {
				let sortByValue = breakdown.get('sortByValue')
				let bdSortOpts, label

				//setup the breakdown sort by value options
				let breakdownOrder = index == 0 ? 'first' : 'second'
				this.$(`.${breakdownOrder}-breakdown-element-container`).toggleClass('is-not-displayed', !isSorted)

				let range = this.stack.get('repeatBy').timeRange()
				if (range != null && breakdown.isTimeBasedBreakdown(this.model)) {
					let periods = rangeToPeriods(range)
					bdSortOpts = temporalIdLabelOptionsForSorting(periods, range.relative)
					label = i18n.get('report.designer.periodForSorting')
					breakdown.ensureValidStaticSortDate()
				} else {
					//get the label the breakdown is breaking down by
					let itemLabel = this.model.columnDataByFieldIdOrColumnType(breakdown.get('breakdownBy')).label
					label = i18n.get('report.designer.itemForSorting', itemLabel)
					//the values repeating by are the options to choose from
					bdSortOpts = nonTemporalIdLabelOptionsForSorting(breakdown)
				}
				this.$(`.${breakdownOrder}-breakdown-element-label`).html(label)
				bdSortOpts.push({id: ALL_OPTION_ID, label: i18n.get('common.all')})

				if (_.isNil(sortByValue)) {
					showAggregationSelection = true
				}
				//null is used to on the backend to denote every value, but that
				//doesn't translate well to the option value, so we use a special value
				let selectedValue = _.isNil(sortByValue) ? ALL_OPTION_ID : sortByValue
				dropdown.idLabels(this.$(`[name=${breakdownOrder}BreakdownValue]`).empty(),
						bdSortOpts, selectedValue)
			})

			//setup the aggregation dropdown
			this._renderSortBreakdownAggregation()
		}

		dropdown.idLabels(this.$('[name=groupSortOrder]').empty(), sortOptions,
			this.column.get('groupingSortType') || 'ascending')
	},

	_renderIndent: function() {
		if (!this.column.showHierarchyIndentOption(this.model)) { return }

		const disableMessageCode = this.column.validateHierarchyIndent(this.model, this.stack)
		const disabled = !_.isNil(disableMessageCode)
		this.$('.indent-hierarchy-container').removeClass('is-not-displayed')

		_.invoke(this.indentHierarchyToggle, 'remove')
		let checked = !!this.column.get('indentHierarchy') && !disabled

		this.indentHierarchyToggle = new OnOffToggle({
			label: i18n.get('report.designer.indentToShowHierarchy'),
			checked,
			name: 'indentHierarchyToggle',
			disabled,
			isMini: true
		})

		this.listenTo(this.indentHierarchyToggle, 'disabled', val => {
			_.invoke(this.disabledIndentTooltip, 'remove')
			this.disabledIndentTooltip = new Tooltip({
				attachTo: this.indentHierarchyToggle.$('.toggle-mini'),
				content: i18n.get(disableMessageCode)
			}).render()
		})
		this.listenTo(this.indentHierarchyToggle, 'change', val => {
			if (val) {
				this.column.set({
					sortType: 'tree',
					indentHierarchy: val
				})
			} else {
				this.column.unset('indentHierarchy')
			}
		})
		_.invoke(this.disabledIndentTooltip, 'remove')
		this.$('.indent-hierarchy-toggle').append(this.indentHierarchyToggle.el)
		this.indentHierarchyToggle.render()
	},

	/**
	 * @param type either 'column' or 'breakdown'
	 */
	_renderSummaryType: function({ type }) {
		const columnOrBreakdown = this[type]
		let showValuesForLabel = columnOrBreakdown.has('groupingId') ?
			'report.designer.showGroupsFor' : 'report.designer.showValuesFor'
		this.$('.show-values-for-label').html(i18n.get(showValuesForLabel))

		const options = this.model.getSummaryTypeOptionsList(columnOrBreakdown)
		const summaryTypeOptionId = this.model.getSummaryTypeOptionId(
			columnOrBreakdown.get('summaryType'), columnOrBreakdown.get('groupCalendarId')
		)
		dropdown.idLabels(this.$('[name="summaryType"]'), options, summaryTypeOptionId)

		let displayed = doDisplaySummaryType(options, columnOrBreakdown)
		this.$('.summary-type-container').toggleClass('is-not-displayed', !displayed)

		this.groupedPeriodRangeSelector = new GroupedPeriodRangeSelector({
			model: GroupedPeriodRangeSelector.createModel(columnOrBreakdown),
			usePeriodRanges: false,
			tooltipTemplate: true
		})

		this.$('.group-similar-time-ranges-container')
			.append(this.groupedPeriodRangeSelector.el)
		this.groupedPeriodRangeSelector.render()

		this.$('.group-similar-time-ranges-container').toggleClass(
			'is-not-displayed', summaryTypeOptionId !==
				'similar-time-ranges')

		this.listenTo(this.groupedPeriodRangeSelector, 'change', () => {
			// #50489 get the current column or breakdown here as it may have
			// changed if the report rerendered
			this[type].set(this.groupedPeriodRangeSelector.idsAndSummaryType())
		})
	},

	_renderBreakdownOption: function() {
		let isAllowingRepeatBy = this.headerCell.allowRepeatBy()
		this.$('.repeat-by-container').toggleClass('is-not-displayed', !isAllowingRepeatBy)
		if (isAllowingRepeatBy) {
			let repeatByLabel = this.stack.breakdowns().length == 0 ?
				'report.designer.repeatThisColumnBy' :
				'report.designer.repeatAgainBy'
			let showRepeatByColumn = this.model.isAggregating()
			this.$('.repeat-by').toggleClass('is-not-displayed', !showRepeatByColumn)
				.html(i18n.get(repeatByLabel))
			this.$('.repeat-by-period').toggleClass('is-not-displayed', showRepeatByColumn)
		}
	},

	_renderShowHeaderOption: function(columnOrBreakdownModel) {
		if (this.headerCell.tableStack instanceof MultiColumnTableStack) {
			if (this.headerCell.displayLabelToggle) {
				this.$('.show-label-container').removeClass('is-not-displayed')

				_.invoke(this.showLabelToggle, 'remove')
				let checked = !!columnOrBreakdownModel.get('hideLabel') == false

				this.showLabelToggle = new OnOffToggle({
					label: i18n.get('report.designer.showThisHeader'),
					checked,
					name: 'showLabelToggle',
					isMini: true
				})
				this.listenTo(this.showLabelToggle, 'change', val => {
					columnOrBreakdownModel.set('hideLabel', !val)
				})
				this.$('.show-label-toggle').append(this.showLabelToggle.el)
				this.showLabelToggle.render()
			}
		}
	},

	_editStackCommonOptions: function() {
		let {tableModel, column} = this
		return {
			column, tableModel,
			reportModel: this.model,
			model: this.stack
		}
	},

	_editStack: function(e) {
		let { isGroupingBy, lastUngroupedColumn } = this._groupingContext()

		let editMenu = new EditStackMenu(this._prepEditMenuOptions({
			testDeletable: () => {
				if (isGroupingBy) {
					return i18n.get('report.designer.stopGroupingToRemove')
				} else if (lastUngroupedColumn) {
					return i18n.get('report.designer.cannotRemoveLastUngroupedColumn')
				}
			}
		}))

		let attachTo = this.$(e.currentTarget)[0]
		// If the 'edit' button was clicked while it was disabled (due to loading a new sort),
		// ignore it because the view will be rerendered
		if (attachTo.classList.contains('is-disabled')) {
			return
		}

		this.listenTo(editMenu, 'select-option', () => this.trigger('done'))
		this.editMenuTooltip = new Tooltip({
			attachTo,
			content: editMenu,
			placement: 'right',
			arrow: 'top',
			extraClass: 'has-bootslide-menu'
		}).render()

		// Remove the editMenuTooltip (#41015) when it loses focus. Normally we'd use
		// closeOnChangedFocus but this case is different since this is a tooltip
		// springing from another tooltip.
		this.$el.one('click', e => {
			if (e.target == attachTo) return
			this.editMenuTooltip.remove()
		})
	},

	onToggleSort: function(show) {
		this.$('.edit').addClass('is-disabled')
		let hide = !show
		let noRepeatBy = !this.stack.has('repeatBy')
		this.$('.sort-select-container').toggleClass('is-not-displayed', hide)
		this.$('.breakdown-element-container').toggleClass('is-not-displayed', hide || noRepeatBy)
		if (hide) {
			this._updateSort(null)
		} else {
			this._onChangeSortOrder({currentTarget: this.$('[name=sortOrder]')})
		}
		this._renderSortBreakdownAggregation()
	},

	_generateAddStackPreview(fieldIdOrColumnType, opts = {}) {
		let { model } = this
		let optionData = model.columnOptionsList(this.stack, opts)
		return new ReportColumnChooser({optionData,
			fieldIdOrColumnType: opts.asRepeatBy ? null : fieldIdOrColumnType,
			dataset: model.get('dataset'),
			asRepeatBy: opts.asRepeatBy})
	},

	_onClickRepeatBy: function(e) {
		let { column, stack } = this
		let columnFieldIdOrColumnType = column ? this.column.fieldIdOrColumnType() : null
		let repeatByMenu = this._generateAddStackPreview(columnFieldIdOrColumnType,
			{asRepeatBy: true})

		_.invoke(this.repeatByTooltip, 'remove')
		this.repeatByTooltip = new Tooltip({
			attachTo: this.el,
			content: repeatByMenu,
			placement: 'right',
			arrow: 'top',
			closeOnAttachedToClick: false,
			extraClass: 'has-bootslide-menu designer-dataset-field-chooser'
		}).render()

		repeatByMenu.once('details', (fieldIdOrColumnType) => {
			this._renderRepeatByForm(repeatByMenu.optionData, fieldIdOrColumnType)
		})

		repeatByMenu.once('select', fieldIdOrColumnType => {
			stack.addBreakdown(this.model
				.defaultBreakdownDataByFieldIdOrColumnType(fieldIdOrColumnType))
			repeatByMenu.trigger('done')
		})

		//signal we're done with the view
		repeatByMenu.once('done', () => this.trigger('done'))
	},

	_onClickRepeatByPeriod: function(e){
		this.stack.addBreakdown({range: defaultRange()})
		this.trigger('done')
	},

	_renderRepeatByForm: function(optionData, fieldIdOrColumnType) {
		let breakdown = this.model
				.defaultBreakdownDataByFieldIdOrColumnType(fieldIdOrColumnType)
		let repeatByForm = new RepeatByForm(this._prepEditMenuOptions({
			optionData,
			breakdown,
			dataset: this.model.get('dataset')
		}))
		appDisplay.dialog(repeatByForm)
		repeatByForm.once('done', (breakdown) => {
			this.stack.addBreakdown(breakdown)
			this.trigger('done')
		})
	},

	/**
	 * Internal method to return the options used to pass into various edit
	 * menus that are launched by this preview.  Callers pass in the options
	 * specific for the view the options are for, then this method fills in the
	 * common options for all view uses the "EditStackMenu" as a base view
	 */
	_prepEditMenuOptions: function(specificViewOptions = {}) {
		let {tableModel, column} = this
		return _.defaults(specificViewOptions, {
			column, tableModel,
			reportModel: this.model,
			model: this.stack
		})
	},

	_onChangeSortOrder: function(e) {
		this._updateSort(this.$(e.currentTarget).val())
	},

	_onChangeGroupSortOrder: function(e) {
		let type = this.$(e.currentTarget).val() || 'ascending'
		this.column.set('groupingSortType', type)
	},

	_onChangeSummaryType: function(e) {
		const summaryType = this.$(e.currentTarget).val()

		this.$('.group-similar-time-ranges-container').toggleClass(
			'is-not-displayed', summaryType !== 'similar-time-ranges')

		// User has selected this option but not necessarily made any selections
		// for the breakdown and group ranges. Default them to the displayed
		// values, in case the user never does.
		const columnAttrs = ['breakdownCalendarId', 'groupCalendarId', 'summaryType']
		let summaryTypeRecord = _.pick(this.column.attributes, ...columnAttrs)
		summaryTypeRecord.summaryType = summaryType

		if (summaryType === 'similar-time-ranges') {
			const { calendarId, subCalendarId } =
				getFirstValidCalendarAndSubCalendarIds()
			if (_.isNil(summaryTypeRecord.groupCalendarId)) {
				summaryTypeRecord.groupCalendarId = calendarId
			}
			if (_.isNil(summaryTypeRecord.breakdownCalendarId)) {
				summaryTypeRecord.breakdownCalendarId = subCalendarId
			}
		} else if (summaryType.startsWith('calendar-')) {
			// If this is the case, the user has really selected more than a summary
			// type; the calendar choice is in there too, and we have to separate
			// the two from each other.
			summaryTypeRecord.summaryType = 'calendar'
			summaryTypeRecord.groupCalendarId = summaryType.split('-')[1]
			delete summaryTypeRecord.breakdownCalendarId
		} else {
			delete summaryTypeRecord.breakdownCalendarId
			delete summaryTypeRecord.groupCalendarId
		}

		// #56334 unset all the properties that weren't set
		_.each(columnAttrs, attr => {
			if (_.isNil(summaryTypeRecord[attr])) this.column.unset(attr)
		})
		this.column.set(summaryTypeRecord)
	},

	_onChangeBreakdownSortValue: function(e, opts = {}) {
		let select = this.$(e.target)
		let sortByValue = select.val()
		let breakdownIndex = select.data('breakdownIndex')
		let breakdown = this.stack.breakdowns()[breakdownIndex]
		if (sortByValue == ALL_OPTION_ID) {
			breakdown.unset('sortByValue')
			breakdown.unset('sortByColumnIndex')
		} else {
			breakdown.set('sortByValue', sortByValue)
			let columnIndex = null
			let selectedOption = select.find(`[data-value="${sortByValue}"]`)
			if (selectedOption.length) {
				columnIndex = selectedOption.data('index')
			}
			breakdown.set('sortByColumnIndex', columnIndex)
		}

		this._renderSortBreakdownAggregation()
	},

	_onChangeSortAggregationType: function(e) {
		let select = this.$(e.target)
		let sortAggregationType = select.val()
		this.stack.get('repeatBy').set('sortAggregationType', sortAggregationType)
	},

	_renderSortBreakdownAggregation: function() {
		let isSorted = this.column.isSorting(this.model)
		let isSortingByEveryValue = _.some(this.stack.breakdowns(),
			breakdown => _.isNil(breakdown.get('sortByValue')))
		const dataType = this.column.resolveDataType(this.model)
		const sortAggOptions = aggregationTypeOptions.breakdownSortAggregation(dataType)

		if (this.stack.isRepeatByStack()) {
			dropdown.idLabels(this.$(`[name=sortAggregationType]`).empty(),
				sortAggOptions,
				this.stack.get('repeatBy').get('sortAggregationType'))
		}

		//HIDE the sort aggregation if ANY of these is true
		//1. none of the breakdowns are sorting by every value
		//2. there are no sorting aggregation options to choose from
		//3. the column isn't sorted
		//4. the stack isn't a repeat by stack
		this.$('.breakdown-aggregation-container').toggleClass('is-not-displayed',
			!isSortingByEveryValue || _.isEmpty(sortAggOptions) || !isSorted
			|| !this.stack.isRepeatByStack())
	},

	_updateSort: function(sortType) {
		if (sortType != null) {
			this.column.set('sortType', sortType)
		} else {
			this.column.unset('sortType')
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.editMenuTooltip, this.sortToggle, this.showLabelToggle,
			this.groupToggle, this.disabledMessageTooltip, this.repeatByTooltip,
			this.showIndividualRecordsToggle, this.groupedPeriodRangeSelector,
			this.disabledIndentTooltip],
			'remove')
	}

})

const getSortOptions = (dataType) => {
	if (isGeographic(dataType)) dataType = ROLLUP

	let sortLabelOptions = {
		ascending: {
			[ROLLUP]: i18n.get('common.search.sort.alpha.asc'),
			[TEXT]: i18n.get('common.search.sort.alpha.asc'),
			[NUMBER]: i18n.get('common.search.sort.number.asc'),
			[DATE]: i18n.get('common.search.sort.date.asc'),
			[YES_NO]: i18n.get('common.search.sort.yesNo.asc'),
			[PERCENT]:i18n.get('common.search.sort.number.asc'),
			[TIME]: i18n.get('common.search.sort.date.asc'),
			[DATE_TIME]: i18n.get('common.search.sort.date.asc'),
			[CALENDAR_PERIOD]: i18n.get('common.search.sort.date.asc'),
			[CURRENCY]: i18n.get('common.search.sort.number.asc')
		},
		descending: {
			[ROLLUP]: i18n.get('common.search.sort.alpha.desc'),
			[TEXT]: i18n.get('common.search.sort.alpha.desc'),
			[NUMBER]: i18n.get('common.search.sort.number.desc'),
			[DATE]: i18n.get('common.search.sort.date.desc'),
			[YES_NO]: i18n.get('common.search.sort.yesNo.desc'),
			[PERCENT]:i18n.get('common.search.sort.number.desc'),
			[TIME]: i18n.get('common.search.sort.date.desc'),
			[DATE_TIME]: i18n.get('common.search.sort.date.desc'),
			[CALENDAR_PERIOD]: i18n.get('common.search.sort.date.desc'),
			[CURRENCY]: i18n.get('common.search.sort.number.desc')
		}
	}

	//setup sorting options
	return [{
		id: 'ascending',
		label: sortLabelOptions.ascending[dataType]
	}, {
		id: 'descending',
		label: sortLabelOptions.descending[dataType]
	}]
}

const doDisplaySummaryType = (options, columnOrBreakdown) => {
	return options.length != 1 &&
		(columnOrBreakdown.get('aggregationType') == 'none' ||
		!_.isNil(columnOrBreakdown.get('groupingId')))
}

const setColumnsToValueSummaryType = (column, model) => {
	_.each(model.allColumns(), c => {
		// #51216 change any other columns using group similar
		// time ranges back to default so they can be grouped on
		if (isGroupSimilarTimeRanges(c.get('summaryType')) &&
			c.get('id') != column.get('id')) {
			c.set('summaryType', VALUE)
		}
	})
}

const addGroupBy = (column, model, groupBy) => {
	setColumnsToValueSummaryType(column, model)

	//generate new id used to identify this stack as grouping
	let groupingId = `g${column.cid}`

	// #54246 you can't have filters on grouped columns, so if we are grouping
	// again on a column that currently has filters, we need to remove them here
	// so they don't carry over
	column.set('datasetFilters', [])

	column.set('groupingId', groupingId, {silent: true})
	groupBy.push(groupingId)
	model.set('groupBy', groupBy)
}

export default EditStackPreview
export { setColumnsToValueSummaryType }
export { addGroupBy }
export { getSortOptions }
export { doDisplaySummaryType }
export { nonTemporalIdLabelOptionsForSorting }
EditStackPreview.__filename__ = 'reports/common/edit-stack-preview.js'
